import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, uploadFile } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useT } from "@/i18n";

const EMPTY = { title: "", title_en: "", area: "casa-villa", city: "", review_text: "", review_text_en: "", review_author: "", before_file_id: null, after_file_id: "", published: true, order: 0 };
const AREAS = ["casa-villa", "interni", "365"];

function ImagePick({ label, value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { onChange((await uploadFile(f)).id); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div>
      <label className="label-craft">{label}</label>
      <div className="flex items-center gap-3">
        {value && <img src={fileUrl(value)} alt="" className="h-16 w-24 rounded-lg object-cover border border-craft" />}
        <input data-testid={testId} type="file" accept="image/*" className="input-craft !py-2 text-xs" disabled={busy} onChange={pick} />
      </div>
    </div>
  );
}

export default function AdminShowcase() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/showcase").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const S = t("admin.showcase");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (editId) await api.put(`/admin/showcase/${editId}`, form); else await api.post("/admin/showcase", form);
      toast.success(S.saved); setForm(EMPTY); setEditId(null); load();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const remove = async (id) => { if (!window.confirm(S.deleteConfirm)) return; await api.delete(`/admin/showcase/${id}`); toast.success(S.deleted); load(); };
  const edit = (x) => { setEditId(x.id); setForm({ ...EMPTY, ...x }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <Shell wide>
      <div className="rise"><p className="eyebrow">{t("admin.backoffice")}</p><h1 className="mt-2 font-serif text-4xl sm:text-5xl font-bold">{S.title}</h1><p className="mt-2 text-ink/60">{S.subtitle}</p></div>
      <div className="mt-8 grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 card-craft p-6 space-y-3 h-fit" data-testid="showcase-form">
          <h2 className="font-semibold">{editId ? S.edit : S.new}</h2>
          <div><label className="label-craft">{S.titleIt}</label><input data-testid="showcase-title" className="input-craft" required value={form.title} onChange={set("title")} /></div>
          <div><label className="label-craft">{S.titleEn}</label><input data-testid="showcase-title-en" className="input-craft" value={form.title_en} onChange={set("title_en")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-craft">{S.area}</label><select data-testid="showcase-area" className="input-craft" value={form.area} onChange={set("area")}>{AREAS.map((a) => <option key={a}>{a}</option>)}</select></div>
            <div><label className="label-craft">{S.city}</label><input data-testid="showcase-city" className="input-craft" value={form.city} onChange={set("city")} /></div>
          </div>
          <div><label className="label-craft">{S.review}</label><textarea data-testid="showcase-review" className="input-craft min-h-[90px]" required value={form.review_text} onChange={set("review_text")} /></div>
          <div><label className="label-craft">{S.reviewEn}</label><textarea data-testid="showcase-review-en" className="input-craft min-h-[70px]" value={form.review_text_en} onChange={set("review_text_en")} /></div>
          <div><label className="label-craft">{S.author}</label><input data-testid="showcase-author" className="input-craft" required value={form.review_author} onChange={set("review_author")} /></div>
          <ImagePick label={S.before} value={form.before_file_id} onChange={(id) => setForm({ ...form, before_file_id: id })} testId="showcase-before-file" />
          <ImagePick label={S.after} value={form.after_file_id} onChange={(id) => setForm({ ...form, after_file_id: id })} testId="showcase-after-file" />
          <label className="flex items-center gap-2 text-sm"><input data-testid="showcase-published" type="checkbox" checked={form.published} onChange={set("published")} /> {S.published}</label>
          <button data-testid="showcase-submit" className="btn-primary w-full" disabled={busy || !form.after_file_id}><Plus size={16} /> {S.save}</button>
          {editId && <button type="button" className="btn-ghost w-full text-xs" onClick={() => { setEditId(null); setForm(EMPTY); }}>{t("common.cancel")}</button>}
        </form>
        <div className="lg:col-span-3 space-y-3" data-testid="showcase-list">
          {items.length === 0 && <p className="card-craft p-8 text-center text-sm text-ink/50">{S.empty}</p>}
          {items.map((x) => (
            <div key={x.id} className="card-craft p-4 flex items-center gap-4" data-testid={`showcase-row-${x.id}`}>
              <img src={fileUrl(x.after_file_id)} alt="" className="h-16 w-24 rounded-lg object-cover border border-craft flex-none" />
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{x.title} {!x.published && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase text-ink/50"><EyeOff size={12} /> {S.hidden}</span>}</div><div className="text-xs text-ink/55 truncate">{x.area} · {x.city} · {x.review_author}</div></div>
              <button className="btn-ghost !p-2" onClick={() => edit(x)} data-testid={`showcase-edit-${x.id}`}><Pencil size={16} /></button>
              <button className="btn-ghost !p-2 text-red-700" onClick={() => remove(x.id)} data-testid={`showcase-delete-${x.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}
